import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const EditIssue = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    status: 'Open',
    assigned_to: ''
  });
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [originalIssue, setOriginalIssue] = useState(null);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchIssue();
      fetchUsers();
    }
  }, [id, isAuthenticated, authLoading]);

  const fetchIssue = async () => {
    try {
      setLoading(true);
      console.log('Fetching issue for editing:', id);
      const response = await axios.get(`/issues/${id}`);
      const issue = response.data;
      
      setOriginalIssue(issue);
      setFormData({
        title: issue.title,
        description: issue.description,
        priority: issue.priority,
        status: issue.status,
        assigned_to: issue.assigned_to || ''
      });
      
      console.log('Issue loaded for editing:', issue.title);
    } catch (error) {
      setError('Failed to load issue details');
      console.error('Error loading issue:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.log('Could not fetch users for assignment');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError('');

    try {
      console.log('Updating issue:', id, formData);
      const submitData = {
        ...formData,
        assigned_to: formData.assigned_to || null
      };

      const response = await axios.put(`/issues/${id}`, submitData);
      console.log('Issue updated successfully');
      
      // Redirect to the updated issue
      navigate(`/issues/${id}`);
    } catch (error) {
      console.error('Error updating issue:', error);
      setError(error.response?.data?.message || 'Failed to update issue');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Check permissions
  const canEdit = originalIssue && (user?.role === 'Admin' || originalIssue.creator_id === user?.id);

  if (authLoading || loading) {
    return (
      <div className="main-content">
        <div className="flex items-center justify-center py-8">
          <div className="spinner"></div>
          <span className="ml-2">Loading issue...</span>
        </div>
      </div>
    );
  }

  if (error && !originalIssue) {
    return (
      <div className="main-content">
        <div className="alert alert-danger">
          {error}
        </div>
        <Link to="/issues" className="btn btn-outline">
          Back to Issues
        </Link>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="main-content">
        <div className="alert alert-warning">
          <h3>🚫 Permission Denied</h3>
          <p>You don't have permission to edit this issue.</p>
          <p>Only the issue creator or admins can edit issues.</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/issues/${id}`} className="btn btn-primary">
            View Issue
          </Link>
          <Link to="/issues" className="btn btn-outline">
            Back to Issues
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Edit Issue #{id}</h1>
        <p className="text-gray-600">Update issue details and information</p>
      </div>

      <div className="card">
        <div className="card-body">
          {error && (
            <div className="alert alert-danger mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="title">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter a clear, concise title for the issue"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                className="form-control textarea"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Provide a detailed description of the issue"
                rows={6}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label" htmlFor="priority">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  className="form-control form-select"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
                <div className="text-sm text-gray-600 mt-1">
                  {formData.priority === 'Low' && 'Minor issues that don\'t affect functionality'}
                  {formData.priority === 'Medium' && 'Standard issues that should be addressed'}
                  {formData.priority === 'High' && 'Important issues affecting user experience'}
                  {formData.priority === 'Critical' && 'Urgent issues that block functionality'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="form-control form-select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>

            {users.length > 0 && (
              <div className="form-group">
                <label className="form-label" htmlFor="assigned_to">
                  Assign To
                </label>
                <select
                  id="assigned_to"
                  name="assigned_to"
                  className="form-control form-select"
                  value={formData.assigned_to}
                  onChange={handleChange}
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <>
                    <div className="spinner"></div>
                    Updating Issue...
                  </>
                ) : (
                  'Update Issue'
                )}
              </button>
              
              <Link
                to={`/issues/${id}`}
                className="btn btn-outline"
              >
                Cancel
              </Link>
              
              <Link
                to="/issues"
                className="btn btn-secondary"
              >
                Back to Issues
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Issue Information */}
      {originalIssue && (
        <div className="card mt-6">
          <div className="card-header">
            <h3 className="card-title">Issue Information</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Created by:</span> {originalIssue.creator_name}
              </div>
              <div>
                <span className="font-medium">Created on:</span> {new Date(originalIssue.created_at).toLocaleDateString()}
              </div>
              <div>
                <span className="font-medium">Last updated:</span> {new Date(originalIssue.updated_at).toLocaleDateString()}
              </div>
              <div>
                <span className="font-medium">Comments:</span> {originalIssue.comments_count || 0}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditIssue; 